<template>
  <div class="player-stats-maps">

    <!-- ── MAIN TABS ─────────────────────────── -->
    <div class="main-tabs">
      <button
        v-for="tab in mainTabs"
        :key="tab.key"
        class="main-tab"
        :class="{ 'tab-active': activeTab === tab.key }"
        @click="activeTab = tab.key"
      >{{ tab.label }}</button>
    </div>

    <!-- ════════════════════════════════════════════════════ -->
    <!--  SHOT TAB  –  Vertical half-pitch                   -->
    <!-- ════════════════════════════════════════════════════ -->
    <div v-if="activeTab === 'shot'" class="tab-content">
      <template v-if="shots && shots.length">
        <!-- Time pills -->
        <div class="time-pills">
          <span
            v-for="(shot, idx) in shots"
            :key="'tp'+idx"
            class="time-pill"
            :class="{ 'pill-active': shotIndex === idx }"
            @click="shotIndex = idx"
          >{{ shot.time }}'</span>
        </div>

        <!-- Goal mouth + stats row -->
        <div class="goal-stats-row">
          <div class="mini-goal-wrap">
            <svg viewBox="-30 -30 460 300" class="mini-goal-svg">
              <!-- Light background outside net -->
              <rect x="-30" y="-30" width="460" height="300" fill="#e8e8e8"/>
              <!-- White net area -->
              <rect x="58" y="22" width="284" height="200" fill="#fff" rx="2"/>
              <!-- Gray net grid -->
              <g stroke="#ddd" stroke-width="0.8" opacity="0.75">
                <line v-for="i in 13" :key="'gv'+i" :x1="58+i*20" y1="22" :x2="58+i*20" y2="222"/>
                <line v-for="j in 9" :key="'gh'+j" x1="58" :y1="22+j*22" x2="342" :y2="22+j*22"/>
              </g>
              <!-- Green grass strip at bottom -->
              <rect x="-30" y="222" width="460" height="78" fill="#437E27"/>
              <rect x="-30" y="218" width="460" height="8" fill="#539438"/>
              <!-- Dark gray goal frame -->
              <rect x="52" y="18" width="10" height="208" fill="#999" rx="2"/>
              <rect x="338" y="18" width="10" height="208" fill="#999" rx="2"/>
              <rect x="52" y="18" width="296" height="10" fill="#999" rx="2"/>
              <g v-if="shotBallPos">
                <ellipse :cx="shotBallPos.x+2" :cy="shotBallPos.y+2" rx="12" ry="12" fill="rgba(0,0,0,0.18)"/>
                <circle :cx="shotBallPos.x" :cy="shotBallPos.y" r="12" fill="white"/>
                <circle :cx="shotBallPos.x" :cy="shotBallPos.y" r="9" :fill="getShotColor(currentShot.shotType)"/>
              </g>
            </svg>
          </div>
          <div class="shot-stats">
            <div class="xg-row">
              <div class="xg-item">
                <div class="xg-label">xG</div>
                <div class="xg-value">{{ currentShot.xg ? currentShot.xg.toFixed(2) : '-' }}</div>
              </div>
              <div class="xg-sep"></div>
              <div class="xg-item">
                <div class="xg-label">xGOT</div>
                <div class="xg-value">{{ currentShot.xgot > 0 ? currentShot.xgot.toFixed(2) : '-' }}</div>
              </div>
            </div>
            <div class="stat-sep"></div>
            <div class="detail-row">
              <span class="d-label">Outcome</span>
              <span class="d-val" :style="{ color: getShotColor(currentShot.shotType), fontWeight: 600 }">
                {{ capitalize(currentShot.shotType) }}
              </span>
            </div>
            <div class="detail-row">
              <span class="d-label">Situation</span>
              <span class="d-val">{{ capitalize(currentShot.situation) }}</span>
            </div>
            <div class="detail-row">
              <span class="d-label">Shot type</span>
              <span class="d-val">{{ capitalize(currentShot.bodyPart) }}</span>
            </div>
          </div>
        </div>

        <!-- Vertical half-pitch (shows ~60% of field) -->
        <div class="pitch-half">
          <svg viewBox="0 0 260 202" class="pitch-svg">
            <!-- Background -->
            <rect x="0" y="0" width="260" height="202" fill="#437E27" rx="6"/>
            <!-- Grass stripes (vertical) -->
            <rect v-for="i in 8" :key="'gs'+i" :x="(i-1)*32.5" y="0" width="32.5" height="202" :fill="i%2===0?'#539438':'#437E27'"/>
            <!-- Pitch boundary (ends at center line) -->
            <rect x="10" y="10" width="240" height="184" rx="2" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>
            <!-- Goal -->
            <rect x="112" y="4" width="36" height="6" fill="rgba(255,255,255,0.85)" rx="1"/>
            <!-- Penalty area -->
            <rect x="50" y="10" width="160" height="58" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>
            <!-- Goal area -->
            <rect x="90" y="10" width="80" height="20" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>
            <!-- Penalty spot -->
            <circle cx="130" cy="49" r="2" fill="rgba(255,255,255,0.6)"/>
            <!-- Penalty arc (curves downward, away from goal) -->
            <path d="M 108 68 A 22 22 0 0 0 152 68" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>
            <!-- Center line -->
            <line x1="10" y1="194" x2="250" y2="194" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>
            <!-- Center circle (half visible, curves upward into pitch) -->
            <path d="M 98 194 A 32 32 0 0 1 162 194" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>
            <circle cx="130" cy="194" r="2" fill="rgba(255,255,255,0.5)"/>
            <!-- Corner arcs -->
            <path d="M 10 20 A 10 10 0 0 0 20 10" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>
            <path d="M 240 10 A 10 10 0 0 0 250 20" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>

            <!-- Shot trajectory -->
            <path v-if="shotTrajectory" :d="shotTrajectory" stroke="white" stroke-width="1.5" stroke-dasharray="4 3" stroke-linecap="round" fill="none" opacity="0.9"/>
            <!-- Block X mark -->
            <g v-if="shotBlockImpact">
              <line :x1="shotBlockImpact.x-5" :y1="shotBlockImpact.y-5" :x2="shotBlockImpact.x+5" :y2="shotBlockImpact.y+5" stroke="white" stroke-width="2.5" stroke-linecap="round"/>
              <line :x1="shotBlockImpact.x+5" :y1="shotBlockImpact.y-5" :x2="shotBlockImpact.x-5" :y2="shotBlockImpact.y+5" stroke="white" stroke-width="2.5" stroke-linecap="round"/>
            </g>
            <!-- Shot dots -->
            <g v-for="(shot, idx) in shots" :key="'sd'+idx" @click="shotIndex = idx" style="cursor:pointer">
              <circle
                :cx="shotScaleX(shotPosAcross(shot))" :cy="shotScaleY(shotPosDepth(shot))"
                :r="idx === shotIndex ? 8 : 5"
                :fill="getShotColor(shot.shotType)"
                :stroke="idx === shotIndex ? 'white' : 'none'"
                :stroke-width="idx === shotIndex ? 2 : 0"
                :opacity="idx === shotIndex ? 1 : 0.75"
              />
            </g>
          </svg>
        </div>

        
      </template>
      <div v-else class="empty-state">No shot data available</div>
    </div>

    <!-- ════════════════════════════════════════════════════ -->
    <!--  PASS TAB  –  Horizontal full pitch                 -->
    <!-- ════════════════════════════════════════════════════ -->
    <div v-if="activeTab === 'pass'" class="tab-content">
      <template v-if="passes && passes.length">
        <div class="sub-filters">
          <button
            v-for="f in passFilters"
            :key="f.key"
            class="sub-btn"
            :class="{ 'sub-active': passFilter === f.key }"
            @click="passFilter = f.key; passPage = 0"
          >
            <span v-if="f.dot" class="filter-dot" :style="{background: f.dot}"></span>
            {{ f.label }}
          </button>
        </div>
        <div class="pitch-full">
          <svg viewBox="0 0 520 340" class="pitch-svg">
            <rect x="0" y="0" width="520" height="340" fill="#437E27" rx="8"/>
            <rect v-for="i in 8" :key="'ps'+i" :x="(i-1)*65" y="0" width="65" height="340" :fill="i%2===0?'#539438':'#437E27'"/>
            <rect x="10" y="10" width="500" height="320" rx="2" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <line x1="260" y1="10" x2="260" y2="330" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <circle cx="260" cy="170" r="45" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <circle cx="260" cy="170" r="2.5" fill="rgba(255,255,255,0.6)"/>
            <rect x="10" y="75" width="85" height="190" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <rect x="10" y="115" width="35" height="110" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <circle cx="80" cy="170" r="2" fill="rgba(255,255,255,0.6)"/>
            <path d="M 95 133 A 40 40 0 0 1 95 207" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <rect x="425" y="75" width="85" height="190" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <rect x="475" y="115" width="35" height="110" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <circle cx="440" cy="170" r="2" fill="rgba(255,255,255,0.6)"/>
            <path d="M 425 133 A 40 40 0 0 0 425 207" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <rect x="6" y="150" width="4" height="40" fill="rgba(255,255,255,0.8)" rx="1"/>
            <rect x="510" y="150" width="4" height="40" fill="rgba(255,255,255,0.8)" rx="1"/>
            <path d="M 10 20 A 10 10 0 0 0 20 10" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>
            <path d="M 500 10 A 10 10 0 0 0 510 20" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>
            <path d="M 10 320 A 10 10 0 0 1 20 330" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>
            <path d="M 500 330 A 10 10 0 0 1 510 320" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>

            <!-- ST watermark -->
            <defs>
              <filter id="passBlur">
                <feGaussianBlur stdDeviation="1.5"/>
              </filter>
            </defs>
            <image :href="stLogoUrl" x="220" y="130" width="80" height="80" opacity="0.60" filter="url(#passBlur)" preserveAspectRatio="xMidYMid meet"/>

            <!-- Pass arrows -->
            <g v-for="(p, idx) in pagedPasses" :key="'pa'+idx">
              <line
                :x1="scaleX(getStartX(p))" :y1="scaleY(getStartY(p))"
                :x2="scaleX(getEndX(p))" :y2="scaleY(getEndY(p))"
                :stroke="getPassColor(p)"
                stroke-width="2"
                stroke-linecap="round"
              />
              <polygon
                :points="passArrowHead(p)"
                :fill="getPassColor(p)"
              />
              <circle :cx="scaleX(getStartX(p))" :cy="scaleY(getStartY(p))" r="3.5" :fill="getPassColor(p)"/>
            </g>
          </svg>
        </div>
        
      </template>
      <div v-else class="empty-state">No pass data available</div>
    </div>

    <!-- ════════════════════════════════════════════════════ -->
    <!--  DRIB TAB  –  Horizontal full pitch                 -->
    <!-- ════════════════════════════════════════════════════ -->
    <div v-if="activeTab === 'drib'" class="tab-content">
      <template v-if="dribbles && dribbles.length">
        <div class="sub-filters">
          <button
            v-for="f in dribFilters"
            :key="f.key"
            class="sub-btn"
            :class="{ 'sub-active': dribFilter === f.key }"
            @click="dribFilter = f.key; dribPage = 0"
          >
            <span v-if="f.dot" class="filter-dot" :style="{background: f.dot}"></span>
            {{ f.label }}
          </button>
        </div>
        <div class="pitch-full">
          <svg viewBox="0 0 520 340" class="pitch-svg">
            <rect x="0" y="0" width="520" height="340" fill="#437E27" rx="8"/>
            <rect v-for="i in 8" :key="'ds'+i" :x="(i-1)*65" y="0" width="65" height="340" :fill="i%2===0?'#539438':'#437E27'"/>
            <rect x="10" y="10" width="500" height="320" rx="2" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <line x1="260" y1="10" x2="260" y2="330" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <circle cx="260" cy="170" r="45" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <circle cx="260" cy="170" r="2.5" fill="rgba(255,255,255,0.6)"/>
            <rect x="10" y="75" width="85" height="190" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <rect x="10" y="115" width="35" height="110" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <circle cx="80" cy="170" r="2" fill="rgba(255,255,255,0.6)"/>
            <path d="M 95 133 A 40 40 0 0 1 95 207" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <rect x="425" y="75" width="85" height="190" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <rect x="475" y="115" width="35" height="110" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <circle cx="440" cy="170" r="2" fill="rgba(255,255,255,0.6)"/>
            <path d="M 425 133 A 40 40 0 0 0 425 207" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <rect x="6" y="150" width="4" height="40" fill="rgba(255,255,255,0.8)" rx="1"/>
            <rect x="510" y="150" width="4" height="40" fill="rgba(255,255,255,0.8)" rx="1"/>
            <path d="M 10 20 A 10 10 0 0 0 20 10" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>
            <path d="M 500 10 A 10 10 0 0 0 510 20" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>
            <path d="M 10 320 A 10 10 0 0 1 20 330" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>
            <path d="M 500 330 A 10 10 0 0 1 510 320" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>

            <!-- ST watermark -->
            <defs>
              <filter id="dribBlur">
                <feGaussianBlur stdDeviation="1.5"/>
              </filter>
            </defs>
            <image :href="stLogoUrl" x="220" y="130" width="80" height="80" opacity="0.60" filter="url(#dribBlur)" preserveAspectRatio="xMidYMid meet"/>

            <!-- Dribble/carry lines -->
            <g v-for="(d, idx) in pagedDribbles" :key="'dd'+idx">
              <line v-if="hasEnd(d)"
                :x1="scaleX(getStartX(d))" :y1="scaleY(getStartY(d))"
                :x2="scaleX(getEndX(d))" :y2="scaleY(getEndY(d))"
                :stroke="getDribColor(d)"
                stroke-width="2"
                :stroke-dasharray="isCarry(d) ? '5 3' : 'none'"
                stroke-linecap="round"
              />
              <circle
                :cx="scaleX(getStartX(d))" :cy="scaleY(getStartY(d))"
                r="5"
                :fill="getDribColor(d)"
                stroke="white" stroke-width="1.2"
              />
              <circle v-if="hasEnd(d)"
                :cx="scaleX(getEndX(d))" :cy="scaleY(getEndY(d))"
                r="3.5"
                :fill="getDribColor(d)"
                opacity="0.8"
              />
            </g>
          </svg>
        </div>
        <div class="map-nav" v-if="filteredDribbles.length > dribPageSize">
          <button class="nav-arrow" @click="dribPage = 0" :disabled="dribPage === 0">&laquo;</button>
          <button class="nav-arrow" @click="dribPage = Math.max(0, dribPage - 1)" :disabled="dribPage === 0">&lsaquo;</button>
          <button class="nav-arrow" @click="dribPage = Math.min(dribMaxPage, dribPage + 1)" :disabled="dribPage >= dribMaxPage">&rsaquo;</button>
        </div>
        
      </template>
      <div v-else class="empty-state">No dribble data available</div>
    </div>

    <!-- ════════════════════════════════════════════════════ -->
    <!--  DEF TAB  –  Horizontal full pitch                  -->
    <!-- ════════════════════════════════════════════════════ -->
    <div v-if="activeTab === 'def'" class="tab-content">
      <template v-if="defActions && defActions.length">
        <div class="sub-filters">
          <button
            v-for="f in defFilters"
            :key="f.key"
            class="sub-btn"
            :class="{ 'sub-active': defFilter === f.key }"
            @click="defFilter = f.key"
          >
            <span v-if="f.dot" class="filter-dot" :style="{background: f.dot}"></span>
            {{ f.label }}
          </button>
        </div>
        <div class="pitch-full">
          <svg viewBox="0 0 520 340" class="pitch-svg">
            <rect x="0" y="0" width="520" height="340" fill="#437E27" rx="8"/>
            <rect v-for="i in 8" :key="'dfs'+i" :x="(i-1)*65" y="0" width="65" height="340" :fill="i%2===0?'#539438':'#437E27'"/>
            <rect x="10" y="10" width="500" height="320" rx="2" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <line x1="260" y1="10" x2="260" y2="330" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <circle cx="260" cy="170" r="45" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <circle cx="260" cy="170" r="2.5" fill="rgba(255,255,255,0.6)"/>
            <rect x="10" y="75" width="85" height="190" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <rect x="10" y="115" width="35" height="110" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <circle cx="80" cy="170" r="2" fill="rgba(255,255,255,0.6)"/>
            <path d="M 95 133 A 40 40 0 0 1 95 207" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <rect x="425" y="75" width="85" height="190" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <rect x="475" y="115" width="35" height="110" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <circle cx="440" cy="170" r="2" fill="rgba(255,255,255,0.6)"/>
            <path d="M 425 133 A 40 40 0 0 0 425 207" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5"/>
            <rect x="6" y="150" width="4" height="40" fill="rgba(255,255,255,0.8)" rx="1"/>
            <rect x="510" y="150" width="4" height="40" fill="rgba(255,255,255,0.8)" rx="1"/>
            <path d="M 10 20 A 10 10 0 0 0 20 10" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>
            <path d="M 500 10 A 10 10 0 0 0 510 20" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>
            <path d="M 10 320 A 10 10 0 0 1 20 330" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>
            <path d="M 500 330 A 10 10 0 0 1 510 320" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.5"/>

            <!-- ST watermark -->
            <defs>
              <filter id="defBlur">
                <feGaussianBlur stdDeviation="1.5"/>
              </filter>
            </defs>
            <image :href="stLogoUrl" x="220" y="130" width="80" height="80" opacity="0.60" filter="url(#defBlur)" preserveAspectRatio="xMidYMid meet"/>

            <!-- Defensive action dots -->
            <g v-for="(d, idx) in filteredDefActions" :key="'da'+idx">
              <circle
                :cx="scaleX(getDefX(d))" :cy="scaleY(getDefY(d))"
                r="6"
                :fill="getDefColor(getDefType(d))"
                stroke="white" stroke-width="1.5"
              />
            </g>
          </svg>
        </div>
        
      </template>
      <div v-else class="empty-state">No defensive data available</div>
    </div>

  </div>
</template>

<script>
import stLogo from '@/assets/st.png';

export default {
  name: "FootballPlayerShotmap",

  props: {
    shots:      { type: Array, default: function() { return []; } },
    passes:     { type: Array, default: function() { return []; } },
    dribbles:   { type: Array, default: function() { return []; } },
    defActions: { type: Array, default: function() { return []; } },
    playerGoals:           { type: Number, default: 0 },
    playerAssists:         { type: Number, default: 0 },
    playerTouches:         { type: Number, default: 0 },
    playerDefContributions:{ type: Number, default: 0 },
    teamSide:   { type: String, default: 'home' },
  },

  data: function() {
    return {
      activeTab: 'shot',
      shotIndex: 0,
      passFilter: 'all',
      passPage: 0,
      passPageSize: 15,
      dribFilter: 'all',
      dribPage: 0,
      dribPageSize: 20,
      defFilter: 'all',
    };
  },

  mounted: function() {
    this.$emit('tab-change', this.activeTab);
  },

  watch: {
    activeTab: function(val) {
      this.$emit('tab-change', val);
    }
  },

  computed: {
    stLogoUrl: function() { return stLogo; },

    mainTabs: function() {
      return [
        { key: 'shot', label: 'Shot' },
        { key: 'pass', label: 'Pass' },
        { key: 'drib', label: 'Drib' },
        { key: 'def',  label: 'Def' },
      ];
    },

    // ── SHOT computeds ──────────────────────
    currentShot: function() {
      return this.shots[this.shotIndex] || this.shots[0] || {};
    },

    shotBallPos: function() {
      var gmc = this.currentShot.goalMouthCoordinates;
      if (!gmc) return null;
      var LP = 62, CB = 28, GR = 222, GW = 276, GH = 194;
      var LP_Y = 55.4, CW = 10.8, CROSSBAR_Z = 24.4;
      var svgX = LP + (LP_Y - gmc.y) / CW * GW;
      var svgY = GR - (gmc.z / CROSSBAR_Z) * GH;
      svgX = Math.max(-20, Math.min(420, svgX));
      svgY = Math.max(CB - 25, Math.min(GR, svgY));
      return { x: svgX, y: svgY };
    },

    shotBlockImpact: function() {
      var s = this.currentShot;
      if ((s.shotType || '').toLowerCase() !== 'block') return null;
      var sx = this.shotScaleX(this.shotPosAcross(s));
      var sy = this.shotScaleY(this.shotPosDepth(s));
      var gx = 130, gy = 10;
      var loc = (s.goalMouthLocation || '').toLowerCase();
      if (loc.includes('left')) gx = 118;
      if (loc.includes('right')) gx = 142;
      var frac = 0.38;
      return {
        x: sx + (gx - sx) * frac,
        y: sy + (gy - sy) * frac,
      };
    },

    shotTrajectory: function() {
      var s = this.currentShot;
      if (!s.draw && !s.playerCoordinates) return '';
      var sx = this.shotScaleX(this.shotPosAcross(s));
      var sy = this.shotScaleY(this.shotPosDepth(s));
      var type = (s.shotType || '').toLowerCase();
      var gx = 130, gy = 10;
      var loc = (s.goalMouthLocation || '').toLowerCase();
      if (loc.includes('left')) gx = 118;
      if (loc.includes('right')) gx = 142;
      if (type === 'miss' && s.draw && s.draw.goal) {
        if (s.draw.goal.x < 38) gx = 110;
        else if (s.draw.goal.x > 62) gx = 150;
      }
      if (type === 'block') {
        var frac = 0.45;
        return 'M ' + sx + ' ' + sy + ' L ' + (sx + (gx - sx) * frac) + ' ' + (sy + (gy - sy) * frac);
      }
      if (type !== 'goal') return 'M ' + sx + ' ' + sy + ' L ' + gx + ' ' + gy;
      var mx = (sx + gx) / 2, my = (sy + gy) / 2;
      var dx = gx - sx, dy = gy - sy;
      var d = Math.hypot(dx, dy) || 1;
      var px = -dy / d, py = dx / d;
      if ((130 - mx) * px + (90 - my) * py < 0) { px = -px; py = -py; }
      var b = Math.min(12, Math.max(4, d * 0.1));
      return 'M ' + sx + ' ' + sy + ' Q ' + (mx + px * b) + ' ' + (my + py * b) + ' ' + gx + ' ' + gy;
    },

    // ── PASS computeds ──────────────────────
    passFilters: function() {
      return [
        { key: 'all', label: 'All passes', dot: null },
        { key: 'accurate', label: 'Accurate', dot: '#10b981' },
        { key: 'inaccurate', label: 'Inaccurate', dot: '#ef4444' },
        { key: 'long', label: 'Long', dot: '#3b82f6' },
      ];
    },
    filteredPasses: function() {
      var self = this;
      if (this.passFilter === 'all') return this.passes;
      if (this.passFilter === 'accurate') return this.passes.filter(function(p) {
        return self.isPassAccurate(p);
      });
      if (this.passFilter === 'inaccurate') return this.passes.filter(function(p) {
        return self.isPassInaccurate(p);
      });
      if (this.passFilter === 'long') return this.passes.filter(function(p) {
        return self.isLongPass(p);
      });
      return this.passes;
    },
    passMaxPage: function() { return Math.max(0, Math.ceil(this.filteredPasses.length / this.passPageSize) - 1); },
    pagedPasses: function() {
      return this.filteredPasses;
    },

    // ── DRIB computeds ──────────────────────
    dribFilters: function() {
      return [
        { key: 'all', label: 'All', dot: null },
        { key: 'successful', label: 'Succ. dribbles', dot: '#10b981' },
        { key: 'carry', label: 'Carries', dot: '#3b82f6' },
      ];
    },
    filteredDribbles: function() {
      var self = this;
      if (this.dribFilter === 'all') return this.dribbles;
      if (this.dribFilter === 'successful') return this.dribbles.filter(function(d) {
        return !self.isCarry(d);
      });
      if (this.dribFilter === 'carry') return this.dribbles.filter(function(d) {
        return self.isCarry(d);
      });
      return this.dribbles;
    },
    dribMaxPage: function() { return Math.max(0, Math.ceil(this.filteredDribbles.length / this.dribPageSize) - 1); },
    pagedDribbles: function() {
      var start = this.dribPage * this.dribPageSize;
      return this.filteredDribbles.slice(start, start + this.dribPageSize);
    },

    // ── DEF computeds ───────────────────────
    defFilters: function() {
      return [
        { key: 'all', label: 'All', dot: null },
        { key: 'tackle', label: 'Tackles', dot: '#f97316' },
        { key: 'interception', label: 'Interceptions', dot: '#ec4899' },
        { key: 'clearance', label: 'Clearances', dot: '#3b82f6' },
        { key: 'recovery', label: 'Recoveries', dot: '#8b5cf6' },
      ];
    },
    filteredDefActions: function() {
      var self = this;
      if (this.defFilter === 'all') return this.defActions;
      return this.defActions.filter(function(d) {
        var t = self.getDefType(d);
        return t === self.defFilter;
      });
    },
  },

  methods: {
    // ── Coordinate helpers for HORIZONTAL pitch (Pass/Drib/Def) ──
    getStartX: function(item) {
      if (item.playerCoordinates) return item.playerCoordinates.x;
      if (item.draw && item.draw.start) return item.draw.start.y;
      if (item.start) return item.start.x;
      return item.x || 0;
    },
    getStartY: function(item) {
      if (item.playerCoordinates) return item.playerCoordinates.y;
      if (item.draw && item.draw.start) return item.draw.start.x;
      if (item.start) return item.start.y;
      return item.y || 0;
    },
    getEndX: function(item) {
      if (item.passEndCoordinates) return item.passEndCoordinates.x;
      if (item.draw && item.draw.end) return item.draw.end.y;
      if (item.end) return item.end.x;
      return this.getStartX(item);
    },
    getEndY: function(item) {
      if (item.passEndCoordinates) return item.passEndCoordinates.y;
      if (item.draw && item.draw.end) return item.draw.end.x;
      if (item.end) return item.end.y;
      return this.getStartY(item);
    },
    hasEnd: function(item) {
      return !!(item.passEndCoordinates || (item.draw && item.draw.end) || item.end);
    },

    // Def coordinates
    getDefX: function(item) {
      if (item.playerCoordinates) return item.playerCoordinates.x;
      if (item.draw && item.draw.start) return item.draw.start.y;
      return item.x || 0;
    },
    getDefY: function(item) {
      if (item.playerCoordinates) return item.playerCoordinates.y;
      if (item.draw && item.draw.start) return item.draw.start.x;
      return item.y || 0;
    },

    // ── Def type detection ──
    getDefType: function(item) {
      var t = (item.eventActionType || item.incidentType || item.type || '').toLowerCase();
      if (t.includes('tackle')) return 'tackle';
      if (t.includes('interception')) return 'interception';
      if (t.includes('clearance')) return 'clearance';
      if (t.includes('recovery')) return 'recovery';
      return t;
    },

    // ── Shot position helpers (vertical half-pitch) ──
    // playerCoordinates.x = distance from attacked goal (0=at goal, ~50=center)
    // playerCoordinates.y = across pitch (0-100)
    shotPosAcross: function(shot) {
      if (shot.playerCoordinates) return shot.playerCoordinates.y;
      if (shot.draw && shot.draw.start) return shot.draw.start.x;
      return 50;
    },
    shotPosDepth: function(shot) {
      if (shot.playerCoordinates) return shot.playerCoordinates.x;
      if (shot.draw && shot.draw.start) return shot.draw.start.y;
      return 20;
    },
    // Scale for vertical half-pitch (viewBox 0 0 260 230)
    shotScaleX: function(across) { return 10 + (across / 100) * 240; },
    shotScaleY: function(depth) { return 10 + (Math.min(depth, 60) / 60) * 184; },

    // ── Pass accuracy helpers ──
    // API uses `outcome: true` for accurate passes; no outcome or false = inaccurate
    isPassAccurate: function(p) {
      return p.outcome === true;
    },
    isPassInaccurate: function(p) {
      return p.outcome !== true;
    },
    isLongPass: function(p) {
      // Crosses are typically long passes
      var eat = (p.eventActionType || '').toLowerCase();
      if (eat === 'cross') return true;
      // Fallback: calculate distance
      var dx = this.getEndX(p) - this.getStartX(p);
      var dy = this.getEndY(p) - this.getStartY(p);
      return Math.hypot(dx, dy) > 32;
    },

    // ── Dribble/carry helpers ──
    isCarry: function(d) {
      var eat = (d.eventActionType || d.incidentType || d.type || '').toLowerCase();
      return eat === 'ball-carry' || eat === 'ballcarry' || eat.includes('carry');
    },

    // ── Color helpers ──
    getShotColor: function(type) {
      return { goal: '#10b981', save: '#60a5fa', miss: '#94a3b8', block: '#f59e0b' }[(type || '').toLowerCase()] || '#94a3b8';
    },
    getPassColor: function(p) {
      // outcome === true → accurate (green), otherwise → inaccurate (red)
      if (p.outcome === true) return '#10b981';
      return '#ef4444';
    },
    getDribColor: function(d) {
      if (this.isCarry(d)) return '#3b82f6';
      return '#10b981';
    },
    getDefColor: function(type) {
      return { tackle: '#f97316', interception: '#ec4899', clearance: '#3b82f6', recovery: '#8b5cf6' }[type] || '#f97316';
    },

    capitalize: function(str) {
      return str ? str.split('-').map(function(w) { return w[0].toUpperCase() + w.slice(1); }).join(' ') : '';
    },

    // Scale for horizontal full pitch (viewBox 0 0 520 340, with 10px padding)
    // Match heatmap coordinate convention: home=flip Y, away=flip X
    scaleX: function(x) {
      var mapped = this.teamSide === 'away' ? 100 - x : x;
      return 10 + (mapped / 100) * 500;
    },
    scaleY: function(y) {
      var mapped = this.teamSide === 'home' ? 100 - y : y;
      return 10 + (mapped / 100) * 320;
    },

    passArrowHead: function(p) {
      var x1 = this.scaleX(this.getStartX(p)), y1 = this.scaleY(this.getStartY(p));
      var x2 = this.scaleX(this.getEndX(p)), y2 = this.scaleY(this.getEndY(p));
      var dx = x2 - x1, dy = y2 - y1;
      var len = Math.hypot(dx, dy) || 1;
      var ux = dx / len, uy = dy / len;
      var sz = 6;
      var ax = x2 - ux * sz, ay = y2 - uy * sz;
      var px = -uy * sz * 0.55, py = ux * sz * 0.55;
      return x2 + ',' + y2 + ' ' + (ax + px) + ',' + (ay + py) + ' ' + (ax - px) + ',' + (ay - py);
    },
  },
};
</script>

<style scoped>
/* ── Container ─────────────────────────── */
.player-stats-maps {
  background: #fff;
  border-top: 1px solid #f0f0f0;
  padding: 8px 10px 10px;
  margin-top: 4px;
}

/* ── Main Tabs ─────────────────────────── */
.main-tabs {
  display: flex;
  gap: 0;
  background: #f0f0f0;
  border-radius: 8px;
  padding: 3px;
  margin-bottom: 10px;
}
.main-tab {
  flex: 1;
  padding: 7px 0;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #888;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  text-align: center;
}
.main-tab:hover { color: #555; }
.tab-active {
  background: #1a1a2e !important;
  color: #fff !important;
}

/* ── Sub-filter pills ──────────────────── */
.sub-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 8px;
}
.sub-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid #e0e0e0;
  background: #fff;
  color: #666;
  cursor: pointer;
  transition: all 0.15s;
}
.sub-btn:hover { border-color: #bbb; }
.sub-active {
  background: #1a1a2e !important;
  color: #fff !important;
  border-color: #1a1a2e !important;
}
.filter-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

/* ── Time Pills ────────────────────────── */
.time-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 8px;
}
.time-pill {
  font-size: 12px;
  font-weight: 500;
  padding: 3px 9px;
  border-radius: 20px;
  cursor: pointer;
  background: #f0f0f0;
  color: #555;
  transition: background 0.15s;
  user-select: none;
}
.time-pill:hover { background: #e0e0e0; }
.pill-active { background: #1a1a1a !important; color: #fff !important; font-weight: 600; }

/* ── Goal + Stats row ──────────────────── */
.goal-stats-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  align-items: stretch;
}
.mini-goal-wrap {
  flex: 0 0 54%;
  background: #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
}
.mini-goal-svg {
  width: 100%;
  height: 100px;
  display: block;
}
.shot-stats {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  padding: 2px 0;
}
.xg-row { display: flex; align-items: center; gap: 6px; }
.xg-item { flex: 1; text-align: center; }
.xg-label { font-size: 10px; text-transform: uppercase; color: #999; font-weight: 600; letter-spacing: 0.5px; }
.xg-value { font-size: 15px; font-weight: 700; color: #111; line-height: 1.2; }
.xg-sep { width: 1px; height: 28px; background: #eee; flex-shrink: 0; }
.stat-sep { height: 1px; background: #f0f0f0; margin: 2px 0; }
.detail-row { display: flex; justify-content: space-between; align-items: baseline; gap: 4px; }
.d-label { font-size: 10px; color: #aaa; text-transform: uppercase; font-weight: 500; white-space: nowrap; }
.d-val { font-size: 11px; color: #222; font-weight: 500; text-align: right; }

/* ── Pitch containers ─────────────────── */
.pitch-half,
.pitch-full {
  border-radius: 10px;
  overflow: hidden;
}
.pitch-svg {
  width: 100%;
  height: auto;
  display: block;
}

/* ── Navigation ────────────────────────── */
.map-nav {
  display: flex;
  justify-content: center;
  gap: 6px;
  padding: 6px 0;
}
.nav-arrow {
  width: 28px;
  height: 28px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fff;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
  transition: background 0.15s;
}
.nav-arrow:hover:not(:disabled) { background: #f5f5f5; }
.nav-arrow:disabled { opacity: 0.3; cursor: default; }

/* ── Bottom Stat ───────────────────────── */
.bottom-stat {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 4px;
  border-top: 1px solid #f0f0f0;
  margin-top: 6px;
  font-size: 13px;
  color: #555;
}
.bottom-stat-val {
  font-weight: 700;
  color: #111;
}

/* ── Empty State ───────────────────────── */
.empty-state {
  text-align: center;
  padding: 30px 10px;
  color: #aaa;
  font-size: 13px;
}

.tab-content {
  min-height: 100px;
}
</style>
